import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, FolderKanban, Zap, CheckSquare, Building2, MessageSquareText, ArrowUpDown, ArrowUp, ArrowDown, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import CalendarView from '@/components/CalendarView';
import { ActionHourTab } from './projects/ActionHourTab';
import { DobDashboardTab } from './projects/DobDashboardTab';
import ClientMeetingNoteModal from './projects/ClientMeetingNoteModal';

const PROJECT_STATUSES = ['Intake', 'Waiting on Client', 'Filing', 'Review', 'Approved', 'Completed', 'Archived'];
const STATUS_ORDER = Object.fromEntries(PROJECT_STATUSES.map((s, i) => [s, i]));
const SORT_OPTIONS = [
  { key: 'default', label: 'Default' },
  { key: 'number', label: 'Project Number' },
  { key: 'name', label: 'Name' },
  { key: 'client', label: 'Client' },
  { key: 'status', label: 'Status' },
  { key: 'upcoming_todo', label: 'Upcoming To-Do' },
];
const statusColors = {
  'Intake': 'bg-blue-500/15 text-blue-400',
  'Waiting on Client': 'bg-amber-500/15 text-amber-400',
  'Filing': 'bg-violet-500/15 text-violet-400',
  'Review': 'bg-cyan-500/15 text-cyan-400',
  'Approved': 'bg-emerald-500/15 text-emerald-400',
  'Completed': 'bg-teal-500/15 text-teal-400',
  'Archived': 'bg-zinc-500/15 text-zinc-400',
};

function todoDueTone(dueDate) {
  if (!dueDate) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + 'T00:00:00').getTime();
  const days = Math.ceil((due - now.getTime()) / 86400000);
  if (days < 0) return { className: 'text-destructive font-semibold', label: `${dueDate} (${Math.abs(days)}d overdue)` };
  if (days === 0) return { className: 'text-destructive font-semibold', label: `${dueDate} (today)` };
  if (days <= 7) return { className: 'text-amber-400 font-medium', label: `${dueDate} (${days}d)` };
  if (days <= 30) return { className: 'text-blue-400', label: `${dueDate} (${days}d)` };
  return { className: 'text-muted-foreground', label: `${dueDate} (${days}d)` };
}

function ProjectTable({ projects, onOpen, emptyMsg, showTodoDue, todoDueMap }) {
  return (
    <Card className="border-border bg-card shadow-none">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider">Number</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Client</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">PM(s)</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
              {showTodoDue && <TableHead className="text-xs uppercase tracking-wider">Next To-Do Due</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.length === 0 ? (
              <TableRow><TableCell colSpan={showTodoDue ? 6 : 5} className="text-center text-muted-foreground text-xs py-8">{emptyMsg}</TableCell></TableRow>
            ) : projects.map(p => (
              <TableRow key={p.id} className="border-border cursor-pointer hover:bg-secondary/30 transition-colors" onClick={() => onOpen(p)} data-testid={`project-row-${p.id}`}>
                <TableCell className="font-mono text-xs text-primary">{p.project_number}</TableCell>
                <TableCell className="text-sm font-medium">{p.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{p.client_name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{p.pm_name || '-'}</TableCell>
                <TableCell><Badge className={`text-xs ${statusColors[p.status] || ''}`}>{p.status}</Badge></TableCell>
                {showTodoDue && (
                  <TableCell className="text-xs" data-testid={`project-todo-due-${p.id}`}>
                    {(() => {
                      const tone = todoDueTone(todoDueMap[p.id]);
                      if (!tone) return <span className="text-muted-foreground/50">No open to-dos</span>;
                      return <span className={tone.className}>{tone.label}</span>;
                    })()}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function ProjectsPage() {
  const { user, hasPerm } = useAuth();
  const canExportCsv = hasPerm('export_projects_csv');
  const [exporting, setExporting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [myProjects, setMyProjects] = useState(false);
  const [pageTab, setPageTab] = useState('active');
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientMeetingModalOpen, setClientMeetingModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [todoDueMap, setTodoDueMap] = useState({}); // { [project_id]: 'YYYY-MM-DD' }
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const fetchProjects = () => api.get('/projects').then(r => setProjects(r.data));

  const handleExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const r = await api.get('/projects/export-csv', { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url;
      a.download = `projects_export_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Projects CSV downloaded');
    } catch (e) {
      const msg = e?.response?.status === 403
        ? 'You do not have permission to export projects.'
        : 'Failed to export CSV.';
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };
  useEffect(() => {
    fetchProjects();
    api.get('/users').then(r => setUsers(r.data)).catch(() => {});
    api.get('/clients').then(r => setClients(r.data)).catch(() => {});
    // Fetch open tasks and compute earliest due date per project (for the "Upcoming To-Do" sort)
    api.get('/tasks').then(r => {
      const map = {};
      (r.data || []).forEach(t => {
        if (!t.project_id || !t.due_date) return;
        if (t.status && ['Complete', 'Completed', 'Done', 'Cancelled'].includes(t.status)) return;
        const prev = map[t.project_id];
        if (!prev || t.due_date < prev) map[t.project_id] = t.due_date;
      });
      setTodoDueMap(map);
    }).catch(() => {});
  }, []);

  // Project detail now lives on a dedicated full page at /projects/:id.
  // Clicking a project row navigates there.
  const openProjectById = (pid, tab = null) => {
    navigate(`/projects/${pid}${tab ? `?tab=${tab}` : ''}`);
  };
  const openDetail = (p) => openProjectById(p.id);

  // Backward-compat redirect: old deep-links used
  // /projects?project=<id>&tab=<tab> (and legacy ?open=<id>) to open the
  // slide-out panel. Repoint those to the new /projects/:id?tab=<tab> route
  // so existing bookmarks and shared links still resolve.
  const legacyPid = searchParams.get('project') || searchParams.get('open');
  const legacyTab = searchParams.get('tab');
  useEffect(() => {
    if (!legacyPid) return;
    navigate(`/projects/${legacyPid}${legacyTab ? `?tab=${legacyTab}` : ''}`, { replace: true });
  }, [legacyPid, legacyTab, navigate]);

  const searchFilter = (p) =>
    (p.project_number || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.client_name || '').toLowerCase().includes(search.toLowerCase());

  const applyFilters = (list) => {
    let filtered = list.filter(searchFilter);
    if (statusFilter !== 'all') filtered = filtered.filter(p => p.status === statusFilter);
    if (clientFilter !== 'all') filtered = filtered.filter(p => p.client_name === clientFilter);
    if (myProjects) filtered = filtered.filter(p => (p.project_manager_ids || []).includes(user?.id));
    if (sortBy !== 'default') {
      const dir = sortDir === 'asc' ? 1 : -1;
      const getKey = (p) => {
        if (sortBy === 'number') return (p.project_number || '').toLowerCase();
        if (sortBy === 'name') return (p.name || '').toLowerCase();
        if (sortBy === 'client') return (p.client_name || '').toLowerCase();
        if (sortBy === 'status') return STATUS_ORDER[p.status] ?? 999;
        if (sortBy === 'upcoming_todo') return todoDueMap[p.id] || '';
        return '';
      };
      filtered = [...filtered].sort((a, b) => {
        const ak = getKey(a);
        const bk = getKey(b);
        // Empty/missing values always sort to the bottom, regardless of direction.
        const aEmpty = ak === '' || ak === null || ak === undefined;
        const bEmpty = bk === '' || bk === null || bk === undefined;
        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        if (ak < bk) return -1 * dir;
        if (ak > bk) return 1 * dir;
        return 0;
      });
    }
    return filtered;
  };

  const uniqueClients = [...new Set(projects.map(p => p.client_name).filter(Boolean))].sort();
  const activeStatuses = PROJECT_STATUSES.filter(s => s !== 'Completed' && s !== 'Archived');

  const activeProjects = applyFilters(projects.filter(p => p.status !== 'Completed' && p.status !== 'Archived'));
  const completedProjects = applyFilters(projects.filter(p => p.status === 'Completed'));

  return (
    <div className="space-y-4 animate-fade-in" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-baseline gap-3 text-[22px] font-medium tracking-tight">Projects<span className="text-sm text-muted-foreground/70 font-normal tabular-nums">{projects.length}</span></h1>
        </div>
        {canExportCsv && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-9 border-border"
            onClick={handleExportCsv}
            disabled={exporting}
            data-testid="projects-export-csv-btn"
            title="Export Active Projects and open Proposals as CSV"
          >
            <Download className="h-3 w-3 mr-1" />
            {exporting ? 'Exporting...' : 'Export as CSV'}
          </Button>
        )}
      </div>

      <Tabs value={pageTab} onValueChange={setPageTab}>
        <TabsList>
          <TabsTrigger value="active" className="text-xs"><FolderKanban className="h-3 w-3 mr-1" /> Active</TabsTrigger>
          <TabsTrigger value="completed" className="text-xs"><CheckSquare className="h-3 w-3 mr-1" /> Completed</TabsTrigger>
          <TabsTrigger value="action-hour" className="text-xs"><Zap className="h-3 w-3 mr-1" /> Action Hour</TabsTrigger>
          <TabsTrigger value="dob" className="text-xs" data-testid="projects-dob-tab"><Building2 className="h-3 w-3 mr-1" /> Compliance Overview</TabsTrigger>
          <TabsTrigger value="calendar" className="text-xs">Calendar</TabsTrigger>
        </TabsList>

        {/* Filters */}
        {(pageTab === 'active' || pageTab === 'completed') && (
          <div className="flex items-center gap-2 flex-wrap mt-3">
            <div className="relative max-w-xs">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border w-56" data-testid="projects-search" />
            </div>
            {pageTab === 'active' && (
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-44 bg-card border-border text-xs h-9" data-testid="projects-status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all" className="text-xs">All Statuses</SelectItem>
                  {activeStatuses.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-44 bg-card border-border text-xs h-9" data-testid="projects-client-filter">
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all" className="text-xs">All Clients</SelectItem>
                {uniqueClients.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-44 bg-card border-border text-xs h-9" data-testid="projects-sort-select">
                <ArrowUpDown className="h-3 w-3 mr-1 text-muted-foreground" />
                <SelectValue placeholder="Sort by..." />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {SORT_OPTIONS.map(o => (
                  <SelectItem key={o.key} value={o.key} className="text-xs" data-testid={`projects-sort-${o.key}`}>
                    {o.key === 'default' ? o.label : `Sort: ${o.label}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sortBy !== 'default' && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-9 border-border px-2"
                onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                data-testid="projects-sort-dir-toggle"
                title={sortDir === 'asc' ? 'Ascending (A→Z, earliest first)' : 'Descending (Z→A, latest first)'}
              >
                {sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
              </Button>
            )}
            <Button
              variant={myProjects ? 'default' : 'outline'}
              size="sm"
              className={`text-xs h-9 ${myProjects ? 'bg-primary text-primary-foreground' : 'border-border'}`}
              onClick={() => setMyProjects(!myProjects)}
              data-testid="my-projects-toggle"
            >
              My Projects
            </Button>
            <Button
              size="sm"
              className="text-xs h-9 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setClientMeetingModalOpen(true)}
              data-testid="add-client-meeting-note-btn"
            >
              <MessageSquareText className="h-3 w-3 mr-1" /> + Meeting Note
            </Button>
            {(search || statusFilter !== 'all' || clientFilter !== 'all' || myProjects || sortBy !== 'default') && (
              <button
                className="text-xs text-primary hover:underline ml-1"
                onClick={() => { setSearch(''); setStatusFilter('all'); setClientFilter('all'); setMyProjects(false); setSortBy('default'); setSortDir('asc'); }}
                data-testid="clear-project-filters"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        <TabsContent value="active" className="mt-3">
          <ProjectTable projects={activeProjects} onOpen={openDetail} emptyMsg="No active projects found." showTodoDue={sortBy === 'upcoming_todo'} todoDueMap={todoDueMap} />
        </TabsContent>

        <TabsContent value="completed" className="mt-3">
          <ProjectTable projects={completedProjects} onOpen={openDetail} emptyMsg="No completed projects." showTodoDue={sortBy === 'upcoming_todo'} todoDueMap={todoDueMap} />
        </TabsContent>

        <TabsContent value="action-hour" className="mt-3">
          <ActionHourTab users={users} />
        </TabsContent>

        <TabsContent value="dob" className="mt-3">
          <DobDashboardTab onOpenProject={(pid, tab) => { if (pid) openProjectById(pid, tab); }} />
        </TabsContent>

        <TabsContent value="calendar" className="mt-3">
          <CalendarView />
        </TabsContent>
      </Tabs>

      <ClientMeetingNoteModal
        open={clientMeetingModalOpen}
        onOpenChange={setClientMeetingModalOpen}
        clients={clients}
        projects={projects}
        users={users}
        onSaved={() => { fetchProjects(); }}
      />
    </div>
  );
}
